import React from 'react';
import {
  Dashboard,
  People,
  Public,
  Language,
  ShoppingCart,
  ShoppingBagOutlined,
  FolderOutlined,
  FilterList,
  MonitorHeart,
  PendingActions,
  AttachMoney,
  AdminPanelSettings,
  History,
  Settings,
  Inventory2,
  Receipt,
} from '@mui/icons-material';
import { RAIL_ITEM_PERMISSION_MAP } from '../../constants/adminPermissions';

export type RailItem = {
  id: string;
  label: string;
  tooltip?: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  section?: 'main' | 'bottom';
};

const ALL_ADMIN_RAIL_ITEMS: RailItem[] = [
  { id: 'home', label: 'Home', path: '/dashboard', icon: <Dashboard />, section: 'main' },
  { id: 'clients', label: 'Clients', path: '/clients', icon: <People />, section: 'main' },
  { id: 'projects', label: 'Projects', path: '/projects', icon: <FolderOutlined />, section: 'main' },
  { id: 'sites', label: 'Sites', tooltip: 'Guest Blog Sites', path: '/sites', icon: <Language />, section: 'main' },
  { id: 'planning', label: 'Planning', path: '/planning', icon: <FilterList />, section: 'main' },
  { id: 'placements', label: 'Orders', path: '/guest-blog-placements', icon: <ShoppingBagOutlined />, section: 'main' },
  { id: 'monitoring', label: 'Monitoring', path: '/live-link-status', icon: <MonitorHeart />, section: 'main' },
  { id: 'publishers', label: 'Publishers', path: '/publishers', icon: <Public />, section: 'main' },
  { id: 'pending', label: 'Site Verifications', path: '/pending-sites', icon: <PendingActions />, section: 'main' },
  { id: 'prices', label: 'Cost Update', path: '/price-change-requests', icon: <AttachMoney />, section: 'main' },
  { id: 'orders', label: 'Transactions', path: '/orders', icon: <ShoppingCart />, section: 'main' },
  { id: 'archive', label: 'Archive', tooltip: 'Restore archived items', path: '/archive', icon: <Inventory2 />, section: 'bottom' },
  { id: 'users', label: 'Users', path: '/users', icon: <AdminPanelSettings />, section: 'bottom' },
  { id: 'audit', label: 'Logs', path: '/audit', icon: <History />, section: 'bottom' },
  { id: 'settings', label: 'Settings', path: '/settings', icon: <Settings />, section: 'bottom' },
];

export function buildAdminRailItems(options: {
  pendingSitesCount?: number;
  pendingPriceCount?: number;
  hasPermission: (key: string) => boolean;
}): RailItem[] {
  const items = ALL_ADMIN_RAIL_ITEMS.filter((item) => {
    const permKey = RAIL_ITEM_PERMISSION_MAP[item.id];
    if (!permKey) return true;
    return options.hasPermission(permKey);
  });

  const pending = items.find((i) => i.id === 'pending');
  if (pending && options.pendingSitesCount) pending.badge = options.pendingSitesCount;
  const prices = items.find((i) => i.id === 'prices');
  if (prices && options.pendingPriceCount) prices.badge = options.pendingPriceCount;

  return items;
}

export function buildAgencyRailItems(): RailItem[] {
  return [
    { id: 'home', label: 'Home', path: '/dashboard', icon: <Dashboard />, section: 'main' },
    { id: 'clients', label: 'Clients', path: '/clients', icon: <People />, section: 'main' },
    { id: 'projects', label: 'Projects', path: '/projects', icon: <FolderOutlined />, section: 'main' },
    { id: 'placements', label: 'Orders', path: '/guest-blog-placements', icon: <ShoppingBagOutlined />, section: 'main' },
    { id: 'monitoring', label: 'Monitoring', path: '/live-link-status', icon: <MonitorHeart />, section: 'main' },
    { id: 'billing', label: 'Billing', path: '/invoices', icon: <Receipt />, section: 'bottom' },
    { id: 'archive', label: 'Archive', tooltip: 'Restore archived items', path: '/archive', icon: <Inventory2 />, section: 'bottom' },
  ];
}

export function buildClientRailItems(options: {
  guestBlogArrivedCount?: number;
  navLocked?: boolean;
}): RailItem[] {
  return [
    { id: 'home', label: 'Home', path: '/dashboard', icon: <Dashboard />, section: 'main' },
    { id: 'projects', label: 'Projects', path: '/projects', icon: <FolderOutlined />, section: 'main' },
    { id: 'orders', label: 'Transactions', path: '/orders', icon: <ShoppingCart />, section: 'main' },
    { id: 'placements', label: 'Orders', path: '/guest-blog-placements', icon: <ShoppingBagOutlined />, section: 'main' },
    { id: 'monitoring', label: 'Monitoring', path: '/live-link-status', icon: <MonitorHeart />, section: 'main' },
    { id: 'billing', label: 'Billing', path: '/billing', icon: <Receipt />, section: 'bottom' },
    { id: 'archive', label: 'Archive', tooltip: 'Restore archived items', path: '/archive', icon: <Inventory2 />, section: 'bottom' },
  ];
}
